/**
 * Formatting helpers shared by the Reach presentational primitives.
 *
 * Deliberately local to `reach/shared/` rather than promoted to `src/lib/`:
 * R5 keeps a new Reach primitive Reach-local until a second consumer exists.
 */

export const EM_DASH = '—';

export function formatShortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The same short date, read back in UTC. `reach_events.starts_at`/`ends_at`
 * are calendar dates stored as UTC-midnight timestamps (a date-only
 * `<input type="date">` round-tripped through `toISOString()` in both event
 * dialogs). Formatting one through the viewer's LOCAL zone rolls it back a
 * day west of UTC: a stored Oct 8 rendered "Oct 7" in the People pool's
 * `Last seen` and in the ingest event picker while the event card — which
 * already formats in UTC — said "Oct 8" on the same screen. Use this for an
 * EVENT DATE; use `formatShortDate` for a real instant (created_at, sent_at).
 */
export function formatEventDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * `2m 14s` / `48s` / `1h 12m`. Used for a job's elapsed time, which is the
 * receipt half of "readout + action + receipt" — a run with no measurable
 * duration returns null so the caller omits the segment rather than printing
 * a fabricated `0s`.
 */
export function formatElapsed(fromIso: string | null | undefined, toMs: number): string | null {
  if (!fromIso) return null;
  const started = new Date(fromIso).getTime();
  if (Number.isNaN(started)) return null;
  const seconds = Math.floor((toMs - started) / 1000);
  if (seconds < 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * A country name at the end of a location, abbreviated.
 *
 * Rosters store what the source gave — "Austin, Texas, United States" — and
 * three quarters of that cell's width goes to a word that is the same on every
 * row. Founder, 2026-08-25: "while we're spelling out the entire country,
 * United States, that should be the country abbreviation code. Keep things
 * clean."
 *
 * This formats at DISPLAY time and never rewrites the stored value: the roster
 * is evidence of what the event handed us, and normalising it in place would
 * lose that. Only a country we can name confidently is abbreviated; anything
 * else is passed through untouched rather than truncated into a guess.
 */
const COUNTRY_ABBREVIATIONS: Record<string, string> = {
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  'united kingdom': 'UK',
  'great britain': 'UK',
  canada: 'CA',
  australia: 'AU',
  germany: 'DE',
  france: 'FR',
  mexico: 'MX',
  india: 'IN',
  japan: 'JP',
  netherlands: 'NL',
  singapore: 'SG',
  ireland: 'IE',
  spain: 'ES',
  italy: 'IT',
  brazil: 'BR',
};

export function formatLocation(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;

  const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return raw;

  const last = parts[parts.length - 1];
  const abbreviated = COUNTRY_ABBREVIATIONS[last.toLowerCase()];
  if (!abbreviated) return raw;

  return [...parts.slice(0, -1), abbreviated].join(', ');
}
